import React, { useState, useEffect } from 'react';
import { exchangeRateService } from '../../services/api';
import { ExchangeRate } from '../../types';
import ExchangeRateForm from './ExchangeRateForm';
import '../../styles/ExchangeRates.css';

const ExchangeRates: React.FC = () => {
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingRate, setEditingRate] = useState<ExchangeRate | null>(null);
  const [showDeleted, setShowDeleted] = useState<boolean>(false);

  useEffect(() => {
    fetchExchangeRates();
  }, [showDeleted]);

  const fetchExchangeRates = async () => {
    setLoading(true);
    try {
      const response = await exchangeRateService.getAll({ 
        includeDeleted: showDeleted 
      });
      console.log('💱 Exchange rates response:', response);
      const ratesData = response.data?.data || response.data || [];
      setExchangeRates(Array.isArray(ratesData) ? ratesData : []);
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExchangeRate = () => {
    setEditingRate(null);
    setShowModal(true);
  };

  const handleEditExchangeRate = (rate: ExchangeRate) => {
    setEditingRate(rate);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRate(null);
  };

  const handleSaveExchangeRate = async (rateData: any) => {
    try {
      if (editingRate) {
        await exchangeRateService.update(editingRate.id, rateData);
      } else {
        await exchangeRateService.create(rateData);
      }
      fetchExchangeRates();
      setShowModal(false);
      setEditingRate(null);
    } catch (error) {
      console.error('Error saving exchange rate:', error);
    }
  };

  const handleDeleteExchangeRate = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this exchange rate? This is a soft delete and can be restored.')) {
      return;
    }
    
    try {
      await exchangeRateService.delete(id);
      fetchExchangeRates();
    } catch (error) {
      console.error('Error deleting exchange rate:', error);
    }
  };

  const handleRestoreExchangeRate = async (id: number) => {
    if (!window.confirm('Are you sure you want to restore this exchange rate?')) {
      return;
    }
    
    try {
      await exchangeRateService.restore(id);
      fetchExchangeRates();
    } catch (error) {
      console.error('Error restoring exchange rate:', error);
    }
  };

  const handlePermanentDeleteExchangeRate = async (id: number) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this exchange rate? This action cannot be undone!')) {
      return;
    }
    
    try {
      await exchangeRateService.permanentDelete(id);
      fetchExchangeRates();
    } catch (error) {
      console.error('Error permanently deleting exchange rate:', error);
    }
  };

  return (
    <div className="exchange-rates-page">
      <div className="exchange-rates-header">
        <h2>Exchange Rates Management</h2>
        <div className="header-actions">
          <label className="toggle-deleted">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Show Deleted
          </label>
          <button className="btn btn-primary" onClick={handleCreateExchangeRate}>
            <i className="fas fa-plus"></i> New Exchange Rate
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading exchange rates...</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table exchange-rates-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Effective Date</th>
                <th>USD to VND Rate</th>
                <th>VND to USD</th>
                <th>Created At</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(exchangeRates) && exchangeRates.length > 0 ? (
                exchangeRates.map(rate => (
                  <tr key={rate.id} className={rate.deleted_at ? 'deleted-row' : ''}>
                    <td>{rate.id}</td>
                    <td>{new Date(rate.effective_date).toLocaleDateString()}</td>
                    <td>{rate.vnd_to_usd.toLocaleString()} VND</td>
                    <td>${(1 / rate.vnd_to_usd).toFixed(6)} USD</td>
                    <td>{new Date(rate.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${rate.deleted_at ? 'deleted' : 'active'}`}>
                        {rate.deleted_at ? 'Deleted' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="rate-actions">
                        {!rate.deleted_at ? (
                          <>
                            <button
                              className="btn-icon edit"
                              onClick={() => handleEditExchangeRate(rate)}
                              title="Edit exchange rate"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn-icon delete"
                              onClick={() => handleDeleteExchangeRate(rate.id)}
                              title="Soft delete exchange rate"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn-icon restore"
                              onClick={() => handleRestoreExchangeRate(rate.id)}
                              title="Restore exchange rate"
                            >
                              <i className="fas fa-undo"></i>
                            </button>
                            <button
                              className="btn-icon permanent-delete"
                              onClick={() => handlePermanentDeleteExchangeRate(rate.id)}
                              title="Permanently delete exchange rate"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="no-rates">
                    {showDeleted ? 'No exchange rates found (including deleted).' : 'No exchange rates found. Add a new exchange rate to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ExchangeRateForm
          exchangeRate={editingRate}
          onSave={handleSaveExchangeRate}
          onCancel={handleCloseModal}
        />
      )}

      <div className="rates-info">
        <h3>About Exchange Rates</h3>
        <p>Exchange rates are used to convert between VND and USD throughout the application.</p>
        <p>When adding a new exchange rate, it will automatically be applied to all new records created after the effective date.</p>
        <p>Historical records will continue to use the exchange rate that was effective at the time they were created.</p>
      </div>
    </div>
  );
};

export default ExchangeRates;
